import { useState, useCallback, useRef } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import {
  Upload,
  Download,
  Trash2,
  Plus,
  X,
  AlertCircle,
  Loader2,
  ZoomIn,
  FileImage,
  Gauge
} from 'lucide-react'

interface ImageFile {
  id: string
  file: File
  name: string
  originalSize: number
  compressedSize: number
  originalUrl: string
  compressedUrl: string
  width: number
  height: number
  status: 'pending' | 'processing' | 'done' | 'error'
  error: string | null
  savedPercent: number
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / 1024 / 1024).toFixed(2) + ' MB'
}

function App() {
  const { t } = useI18n()
  const [files, setFiles] = useState<ImageFile[]>([])
  const [quality, setQuality] = useState(80)
  const [maxWidth, setMaxWidth] = useState(1920)
  const [outputFormat, setOutputFormat] = useState<'original' | 'jpeg' | 'png' | 'webp'>('original')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Process single image
  const processImage = useCallback(async (
    file: File,
    targetQuality: number,
    targetWidth: number,
    format: string
  ): Promise<{ blob: Blob; url: string; width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)

        // Calculate new dimensions
        let { width, height } = img
        if (width > targetWidth) {
          height = Math.round((height * targetWidth) / width)
          width = targetWidth
        }

        // Create canvas
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Canvas not supported'))
          return
        }

        // White background for JPEG (handles transparency)
        if (format === 'image/jpeg') {
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, width, height)
        }

        ctx.drawImage(img, 0, 0, width, height)

        // Determine mime type
        const mimeType = format === 'original'
          ? file.type
          : format

        // Compress
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Compression failed'))
              return
            }
            const url = URL.createObjectURL(blob)
            resolve({ blob, url, width, height })
          },
          mimeType,
          targetQuality / 100
        )
      }
      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = URL.createObjectURL(file)
    })
  }, [])

  // Handle file upload
  const handleFiles = useCallback(async (newFiles: FileList | null) => {
    if (!newFiles) return
    setError('')

    const imageFiles = Array.from(newFiles).filter(f => f.type.startsWith('image/'))
    if (imageFiles.length === 0) {
      setError(t('imgCompress.invalidFile') || 'Please select image files (JPEG, PNG, WebP, GIF)')
      return
    }

    const processed: ImageFile[] = imageFiles.map(file => ({
      id: Math.random().toString(36).substring(2, 15),
      file,
      name: file.name,
      originalSize: file.size,
      compressedSize: 0,
      originalUrl: URL.createObjectURL(file),
      compressedUrl: '',
      width: 0,
      height: 0,
      status: 'pending',
      error: null,
      savedPercent: 0,
    }))

    setFiles(prev => [...prev, ...processed])
  }, [t])

  // Compress all images
  const compressAll = useCallback(async () => {
    const pending = files.filter(f => f.status === 'pending')
    if (pending.length === 0) return

    setIsProcessing(true)
    setError('')

    const formatMap: Record<string, string> = {
      original: 'original',
      jpeg: 'image/jpeg',
      png: 'image/png',
      webp: 'image/webp',
    }
    const targetFormat = formatMap[outputFormat] || 'original'

    for (const item of pending) {
      setFiles(prev => prev.map(f =>
        f.id === item.id ? { ...f, status: 'processing' } : f
      ))

      try {
        const { blob, url, width, height } = await processImage(
          item.file,
          quality,
          maxWidth,
          targetFormat
        )

        const savedPercent = Math.round(((item.originalSize - blob.size) / item.originalSize) * 100)

        setFiles(prev => prev.map(f =>
          f.id === item.id
            ? {
                ...f,
                status: 'done',
                compressedUrl: url,
                compressedSize: blob.size,
                width,
                height,
                savedPercent: Math.max(0, savedPercent),
              }
            : f
        ))
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        setFiles(prev => prev.map(f =>
          f.id === item.id
            ? { ...f, status: 'error', error: msg }
            : f
        ))
      }
    }

    setIsProcessing(false)
  }, [files, quality, maxWidth, outputFormat, processImage])

  // Download single
  const downloadImage = useCallback((item: ImageFile) => {
    if (!item.compressedUrl) return
    const a = document.createElement('a')
    a.href = item.compressedUrl

    // Determine extension
    let ext = item.file.name.split('.').pop() || 'jpg'
    if (outputFormat === 'jpeg') ext = 'jpg'
    else if (outputFormat === 'png') ext = 'png'
    else if (outputFormat === 'webp') ext = 'webp'

    const baseName = item.file.name.replace(/\.[^/.]+$/, '')
    a.download = `${baseName}_compressed.${ext}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }, [outputFormat])

  // Remove file
  const removeFile = useCallback((id: string) => {
    setFiles(prev => {
      const item = prev.find(f => f.id === id)
      if (item) {
        if (item.originalUrl) URL.revokeObjectURL(item.originalUrl)
        if (item.compressedUrl) URL.revokeObjectURL(item.compressedUrl)
      }
      return prev.filter(f => f.id !== id)
    })
  }, [])

  // Clear all
  const clearAll = useCallback(() => {
    files.forEach(f => {
      if (f.originalUrl) URL.revokeObjectURL(f.originalUrl)
      if (f.compressedUrl) URL.revokeObjectURL(f.compressedUrl)
    })
    setFiles([])
    setError('')
  }, [files])

  const totalOriginal = files.reduce((sum, f) => sum + f.originalSize, 0)
  const totalCompressed = files.reduce((sum, f) => sum + (f.compressedSize || f.originalSize), 0)
  const totalSaved = totalOriginal > 0 ? Math.round(((totalOriginal - totalCompressed) / totalOriginal) * 100) : 0

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('imgCompress.title') || 'Image Compressor'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('imgCompress.subtitle') || 'Compress JPEG, PNG, WebP images online. '}
          <span className="text-primary font-medium">{t('imgCompress.privacy') || '100% client-side'}</span>, {t('imgCompress.noUpload') || 'no upload needed'}.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Settings Panel */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Quality */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <Gauge className="w-4 h-4" />
                  {t('imgCompress.quality') || 'Quality'}
                </label>
                <span className="text-sm text-brand-primary font-medium">{quality}%</span>
              </div>
              <input
                type="range"
                min={1}
                max={100}
                value={quality}
                onChange={e => setQuality(Number(e.target.value))}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('imgCompress.qualityDesc') || 'Higher = better quality, larger file'}
              </p>
            </div>

            {/* Max Width */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <ZoomIn className="w-4 h-4" />
                  {t('imgCompress.maxWidth') || 'Max Width'}
                </label>
                <span className="text-sm text-brand-primary font-medium">{maxWidth}px</span>
              </div>
              <input
                type="range"
                min={100}
                max={4000}
                step={100}
                value={maxWidth}
                onChange={e => setMaxWidth(Number(e.target.value))}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('imgCompress.widthDesc') || 'Images larger than this will be resized'}
              </p>
            </div>

            {/* Format */}
            <div>
              <label className="text-sm font-medium text-foreground flex items-center gap-1.5 mb-1.5">
                <FileImage className="w-4 h-4" />
                {t('imgCompress.format') || 'Output Format'}
              </label>
              <div className="flex flex-wrap gap-2">
                {(['original', 'jpeg', 'png', 'webp'] as const).map(fmt => (
                  <button
                    key={fmt}
                    onClick={() => setOutputFormat(fmt)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      outputFormat === fmt
                        ? 'bg-brand-primary text-white'
                        : 'bg-muted text-foreground hover:bg-muted/80'
                    }`}
                  >
                    {fmt === 'original' ? (t('imgCompress.original') || 'Original') : fmt.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault()
            handleFiles(e.dataTransfer.files)
          }}
          className="bg-surface rounded-xl shadow-sm border border-border p-8 md:p-12 text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
            <Upload className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('imgCompress.uploadTitle') || 'Upload Images'}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('imgCompress.uploadDesc') || 'Drag & drop or click to select. Supports JPEG, PNG, WebP, GIF.'}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              {t('imgCompress.addFiles') || 'Add Images'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={e => handleFiles(e.target.files)}
            />
            {files.length > 0 && (
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-background border border-border text-foreground rounded-lg hover:bg-muted transition-colors text-sm font-medium"
              >
                <Trash2 className="w-4 h-4" />
                {t('actions.clear') || 'Clear All'}
              </button>
            )}
          </div>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-4">
            {/* Summary */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-border">
              <div className="text-sm">
                <span className="font-medium text-foreground">{files.length}</span>
                <span className="text-muted-foreground"> {t('imgCompress.files') || 'files'} · </span>
                <span className="font-medium text-foreground">{formatSize(totalOriginal)}</span>
                {totalCompressed > 0 && totalCompressed !== totalOriginal && (
                  <>
                    <span className="text-muted-foreground"> → </span>
                    <span className="font-medium text-green-600 dark:text-green-400">{formatSize(totalCompressed)}</span>
                    <span className="text-xs text-green-600 dark:text-green-400 ml-1">(-{totalSaved}%)</span>
                  </>
                )}
              </div>
              <button
                onClick={compressAll}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('imgCompress.processing') || 'Processing...'}
                  </>
                ) : (
                  <>
                    <Gauge className="w-4 h-4" />
                    {t('imgCompress.compress') || 'Compress All'}
                  </>
                )}
              </button>
            </div>

            {/* Image Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {files.map(item => (
                <div
                  key={item.id}
                  className={`border rounded-lg overflow-hidden ${
                    item.status === 'error'
                      ? 'border-red-200 dark:border-red-800'
                      : item.status === 'done'
                        ? 'border-green-200 dark:border-green-800'
                        : 'border-border'
                  }`}
                >
                  {/* Image Preview */}
                  <div className="aspect-square bg-black/5 dark:bg-white/5 relative overflow-hidden">
                    {item.status === 'done' && item.compressedUrl ? (
                      <img
                        src={item.compressedUrl}
                        alt="Compressed"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <img
                        src={item.originalUrl}
                        alt="Original"
                        className={`w-full h-full object-contain ${item.status === 'processing' ? 'opacity-50' : ''}`}
                      />
                    )}
                    {item.status === 'processing' && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                      </div>
                    )}
                    {item.status === 'done' && item.savedPercent > 0 && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                        -{item.savedPercent}%
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3 space-y-2">
                    <p className="text-sm font-medium text-foreground truncate">{item.name}</p>

                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatSize(item.originalSize)}</span>
                      {item.status === 'done' && (
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          {formatSize(item.compressedSize)}
                        </span>
                      )}
                    </div>

                    {item.status === 'error' && (
                      <p className="text-xs text-red-500">{item.error}</p>
                    )}

                    <div className="flex gap-2 pt-1">
                      {item.status === 'done' && (
                        <button
                          onClick={() => downloadImage(item)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-brand-primary text-white rounded-md hover:opacity-90 transition-opacity text-xs font-medium"
                        >
                          <Download className="w-3.5 h-3.5" />
                          {t('actions.download') || 'Download'}
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(item.id)}
                        className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            {t('security.note')}
          </p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
